<template>
  <div class="modern-gamedev">
    <!-- キューブパーティクル背景 -->
    <div class="cube-particles">
      <div
        v-for="cube in cubes"
        :key="cube.id"
        class="cube"
        :style="{
          left: cube.x + 'px',
          bottom: cube.startY + 'px',
          width: cube.size + 'px',
          height: cube.size + 'px',
          animationDuration: cube.duration + 's',
          animationDelay: cube.delay + 's',
          '--rotation-speed': cube.rotationSpeed + 's',
          '--glow-intensity': cube.glowIntensity,
          '--cube-size': cube.size + 'px',
        }"
      >
        <div class="cube-face cube-front"></div>
        <div class="cube-face cube-back"></div>
        <div class="cube-face cube-right"></div>
        <div class="cube-face cube-left"></div>
        <div class="cube-face cube-top"></div>
        <div class="cube-face cube-bottom"></div>
        <div class="cube-trail"></div>
      </div>
    </div>
    <section class="hero container">
      <div>
        <p class="eyebrow">GAME DEVELOPMENT</p>
        <h1>Unity & Unreal Engine 5 開発実績</h1>
        <p class="lead">
          豊富な実績を持つメンバーが対応可能。あらゆるジャンルに対応したゲーム開発サービスを提供しています。
        </p>
        <div class="actions">
          <router-link class="btn primary" to="/next-contact"
            >お問い合わせ</router-link
          >
          <router-link class="btn ghost" to="/next-services"
            >サービス一覧</router-link
          >
        </div>
      </div>
    </section>

    <section class="section container engines">
      <div class="section__header">
        <p class="eyebrow">SUPPORTED ENGINES</p>
        <h2>対応エンジン</h2>
      </div>
      <div class="engines__grid">
        <article class="engine-card">
          <h3>Unity</h3>
          <div class="engine-card__logo">
            <img src="/image/unity.png" alt="Unity" />
          </div>
          <p>
            2D/3Dゲーム開発、AR/VRアプリケーション、モバイルゲームまで幅広く対応。最新バージョンにも対応しています。
          </p>
          <ul>
            <li>Unity 2021 LTS / 2022 LTS</li>
            <li>Unity C# スクリプティング</li>
            <li>Shader 開発（HLSL/ShaderLab）</li>
            <li>アセットパイプライン最適化</li>
            <li>プラットフォーム別ビルド対応</li>
          </ul>
        </article>
        <article class="engine-card">
          <h3>Unreal Engine 5</h3>
          <div class="engine-card__logo">
            <img src="/image/UE.png" alt="Unreal Engine 5" />
          </div>
          <p>
            様々なゲームジャンルの経験のある技術者が担当します。通信モジュールなど個別の対応も可能です。
          </p>
          <ul>
            <li>UE5.0 / UE5.1 / UE5.2+</li>
            <li>Blueprints / C++ 開発</li>
            <li>Nanite / Lumen 活用</li>
            <li>MetaHuman / Animation パイプライン</li>
            <li>マルチプラットフォーム対応</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="section container capabilities">
      <div class="section__header">
        <p class="eyebrow">CAPABILITIES</p>
        <h2>開発対応範囲</h2>
      </div>
      <div class="capabilities__grid">
        <article v-for="cap in capabilities" :key="cap.title" class="cap-card">
          <h3>{{ cap.title }}</h3>
          <p>{{ cap.desc }}</p>
          <div class="chips">
            <span v-for="tag in cap.tags" :key="tag">{{ tag }}</span>
          </div>
        </article>
      </div>
    </section>

    <section class="section container partner">
      <div class="section__header">
        <p class="eyebrow">PARTNERSHIP</p>
        <h2>協力会社</h2>
      </div>
      <div class="partner__card">
        <div class="partner__info">
          <h3>
            <a
              href="https://sologram.co.jp/"
              target="_blank"
              rel="noopener"
              class="partner__link"
              >ソログラム（東京）</a
            >
          </h3>
          <p>
            ゲーム開発・エンターテインメントコンテンツ制作,Unityに強みを持つ協力会社です。
            プロフェッショナリズム・専門性の高い領域において、連携して開発を進めることができます。
          </p>
          <div class="partner__tags">
            <span>ゲーム開発</span>
            <span>エンターテインメント</span>
            <span>コンテンツ制作</span>
          </div>
        </div>
      </div>
    </section>

    <section class="section container process">
      <div class="section__header">
        <p class="eyebrow">DEVELOPMENT PROCESS</p>
        <h2>開発フロー</h2>
      </div>
      <ol>
        <li v-for="step in process" :key="step.title">
          <p class="step-title">{{ step.title }}</p>
          <p class="step-desc">{{ step.desc }}</p>
        </li>
      </ol>
    </section>

    <section class="section container cta">
      <div class="cta__content">
        <h2>ゲーム開発プロジェクトのご相談</h2>
        <p>
          Unity / Unreal Engine 5
          での開発実績を持つメンバーが対応いたします。まずはお気軽にお問い合わせください。
        </p>
        <div class="actions">
          <router-link class="btn primary large" to="/next-contact"
            >お問い合わせ</router-link
          >
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";

export default {
  name: "ModernGameDev",
  setup() {
    const cubes = ref([]);
    const cubeIdCounter = ref(0);
    let spawnTimer = null;
    let animationFrameId = null;

    const createCube = () => {
      const screenWidth = window.innerWidth;
      const size = Math.random() * 30 + 20;
      const x = Math.random() * (screenWidth - size);
      const duration = Math.random() * 3 + 4;
      const delay = Math.random() * 0.5;
      const rotationSpeed = Math.random() * 2 + 1;
      const glowIntensity = Math.random() * 0.5 + 0.5;

      const cube = {
        id: cubeIdCounter.value++,
        x: x,
        startY: -50,
        size: size,
        duration: duration,
        delay: delay,
        rotationSpeed: rotationSpeed,
        glowIntensity: glowIntensity,
      };

      cubes.value.push(cube);

      setTimeout(() => {
        const index = cubes.value.findIndex((c) => c.id === cube.id);
        if (index !== -1) {
          cubes.value.splice(index, 1);
        }
      }, (duration + delay) * 1000);
    };

    const spawnCubes = () => {
      const spawnNext = () => {
        createCube();
        const nextSpawn = Math.random() * 2000 + 500;
        spawnTimer = setTimeout(spawnNext, nextSpawn);
      };
      spawnNext();
    };

    onMounted(() => {
      spawnCubes();
    });

    onUnmounted(() => {
      if (spawnTimer) {
        clearTimeout(spawnTimer);
      }
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      cubes.value = [];
    });

    const capabilities = ref([
      {
        title: "ゲーム開発",
        desc: "アクション、RPG、パズル、シミュレーションなど、あらゆるジャンルのゲーム開発に対応。",
        tags: ["2D/3D", "マルチプレイ", "ネットワーク", "セーブシステム"],
      },
      {
        title: "モバイルゲーム",
        desc: "iOS / Android 向けの最適化、IAP、広告連携、アナリティクス実装まで対応。",
        tags: ["iOS", "Android", "最適化", "マネタイズ"],
      },
      {
        title: "コンソール対応",
        desc: "PlayStation、Xbox、Nintendo Switch など、各プラットフォームの要件に対応。",
        tags: ["PS5/PS4", "Xbox Series", "Nintendo Switch"],
      },
      {
        title: "ディレクション業務",
        desc: "開発の進行管理、チームマネジメント、ディレクション業務を行います。外部委託による透明性と品質を担保します。",
        tags: [
          "チームマネジメント",
          "プロジェクトマネジメント",
          "ディレクション",
        ],
      },
      {
        title: "パフォーマンス最適化",
        desc: "フレームレート向上、メモリ管理、アセット最適化など、パフォーマンス改善を実施。",
        tags: ["プロファイリング", "最適化", "メモリ管理"],
      },
      {
        title: "ツール開発",
        desc: "エディタ拡張、ビルドパイプライン、自動化ツールなど、開発効率化ツールの作成。",
        tags: ["エディタ拡張", "CI/CD", "自動化"],
      },
    ]);

    const process = ref([
      {
        title: "1. 要件ヒアリング",
        desc: "プロジェクトの目的、ターゲットプラットフォーム、予算、スケジュールなどを詳しくお聞きします。",
      },
      {
        title: "2. 技術選定・設計",
        desc: "Unity / Unreal Engine 5 の選定、アーキテクチャ設計、開発環境の構築を行います。",
      },
      {
        title: "3. プロトタイプ開発",
        desc: "コア機能のプロトタイプを作成し、方向性を確認していただきます。",
      },
      {
        title: "4. 本格開発",
        desc: "アセット制作、プログラミング、UI/UX 実装、テストを並行して進めます。",
      },
      {
        title: "5. 最適化・デバッグ",
        desc: "パフォーマンス最適化、バグ修正、プラットフォーム別テストを実施します。",
      },
      {
        title: "6. リリース・サポート",
        desc: "ストア申請サポート、リリース後のメンテナンス、アップデート対応を行います。",
      },
    ]);

    return {
      capabilities,
      process,
      cubes,
    };
  },
};
</script>

<style lang="scss" scoped>
.modern-gamedev {
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    #4541c7 0%,
    #3d6cb7 10%,
    #30519c 18%,
    #2a4073 60%,
    #314f7e 70%,
    #312e81 78%,
    #312e81 100%
  );
  color: #f1f5f9;
  padding-top: 80px;
  position: relative;
}

.cube-particles {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  perspective: 2000px;
}

.cube {
  position: absolute;
  transform-style: preserve-3d;
  animation: riseUp linear forwards;
  --rotation-speed: 2s;
  --glow-intensity: 0.7;
  perspective: 1000px;
  transform-origin: center center;
}

@keyframes riseUp {
  0% {
    transform: translateY(0) translateZ(0) rotateX(0deg) rotateY(0deg)
      rotateZ(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(calc(-100vh - 100px)) translateZ(0) rotateX(720deg)
      rotateY(720deg) rotateZ(360deg);
    opacity: 0;
  }
}

.cube-face {
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, var(--glow-intensity)) 0%,
    rgba(99, 102, 241, var(--glow-intensity)) 50%,
    rgba(129, 140, 248, var(--glow-intensity)) 100%
  );
  border: 2px solid rgba(147, 197, 253, 0.8);
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.8), 0 0 40px rgba(99, 102, 241, 0.6),
    inset 0 0 20px rgba(147, 197, 253, 0.3);
  animation: rotateCube var(--rotation-speed) linear infinite;
}

@keyframes rotateCube {
  0% {
    transform: rotateZ(0deg);
  }
  100% {
    transform: rotateZ(360deg);
  }
}

.cube-front {
  transform: rotateY(0deg) translateZ(calc(var(--cube-size, 25px) / 2));
}

.cube-back {
  transform: rotateY(180deg) translateZ(calc(var(--cube-size, 25px) / 2));
}

.cube-right {
  transform: rotateY(90deg) translateZ(calc(var(--cube-size, 25px) / 2));
}

.cube-left {
  transform: rotateY(-90deg) translateZ(calc(var(--cube-size, 25px) / 2));
}

.cube-top {
  transform: rotateX(90deg) translateZ(calc(var(--cube-size, 25px) / 2));
}

.cube-bottom {
  transform: rotateX(-90deg) translateZ(calc(var(--cube-size, 25px) / 2));
}

.cube-trail {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150%;
  height: 200%;
  background: linear-gradient(
    to top,
    rgba(59, 130, 246, 0.4) 0%,
    rgba(99, 102, 241, 0.3) 30%,
    rgba(129, 140, 248, 0.2) 60%,
    transparent 100%
  );
  filter: blur(15px);
  pointer-events: none;
  z-index: -1;
  animation: trailFade linear forwards;
}

@keyframes trailFade {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scaleY(0);
  }
  10% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scaleY(1);
  }
}

.hero,
.section {
  position: relative;
  z-index: 1;
}

.hero {
  padding: 6rem 1.5rem;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;

  .eyebrow {
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #818cf8;
    margin-bottom: 1rem;
  }

  h1 {
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    background: linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .lead {
    font-size: 1.25rem;
    line-height: 1.7;
    color: #cbd5e1;
    margin-bottom: 2.5rem;
  }

  .actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
  }
}

.section {
  padding: 5rem 1.5rem;
  max-width: 1200px;
  margin: 0 auto;

  &__header {
    text-align: center;
    margin-bottom: 3rem;

    .eyebrow {
      font-size: 0.875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #818cf8;
      margin-bottom: 1rem;
    }

    h2 {
      font-size: clamp(2rem, 4vw, 3rem);
      font-weight: 700;
      margin-bottom: 1rem;
    }
  }
}

.engines {
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
  }
}

.engine-card {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(129, 140, 248, 0.2);
  border-radius: 20px;
  padding: 2.5rem;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(129, 140, 248, 0.2);
  }

  h3 {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #f1f5f9;
    text-align: center;
  }

  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;

    img {
      max-width: 200px;
      width: 100%;
      height: auto;
      object-fit: contain;
      border-radius: 16px;
      outline: 3px solid rgba(129, 140, 248, 0.6);
      outline-offset: 4px;
      box-shadow: 0 8px 32px rgba(129, 140, 248, 0.4),
        0 4px 16px rgba(0, 0, 0, 0.3);
      filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))
        drop-shadow(0 2px 6px rgba(129, 140, 248, 0.3));
    }
  }

  > p {
    color: #cbd5e1;
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      padding: 0.5rem 0;
      padding-left: 1.5rem;
      position: relative;
      color: #cbd5e1;

      &::before {
        content: "✓";
        position: absolute;
        left: 0;
        color: #818cf8;
        font-weight: bold;
      }
    }
  }
}

.capabilities {
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-top: 3rem;
  }
}

.cap-card {
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(129, 140, 248, 0.15);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s;

  &:hover {
    background: rgba(30, 41, 59, 0.6);
    border-color: rgba(129, 140, 248, 0.3);
    transform: translateY(-2px);
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #f1f5f9;
  }

  > p {
    color: #cbd5e1;
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    span {
      background: rgba(129, 140, 248, 0.2);
      color: #cbd5e1;
      padding: 0.375rem 0.875rem;
      border-radius: 20px;
      font-size: 0.875rem;
      font-weight: 500;
    }
  }
}

.partner {
  &__card {
    background: rgba(30, 41, 59, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(129, 140, 248, 0.2);
    border-radius: 20px;
    padding: 3rem;
    max-width: 800px;
    margin: 0 auto;
  }

  &__info {
    h3 {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 1.5rem;
      color: #f1f5f9;
    }

    > p {
      color: #cbd5e1;
      line-height: 1.8;
      margin-bottom: 2rem;
      font-size: 1.125rem;
    }
  }

  &__link {
    color: #f1f5f9;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #818cf8;
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: center;

    span {
      background: linear-gradient(135deg, #818cf8 0%, #6366f1 100%);
      color: white;
      padding: 0.5rem 1.25rem;
      border-radius: 24px;
      font-size: 0.875rem;
      font-weight: 600;
    }
  }
}

.process {
  ol {
    list-style: none;
    padding: 0;
    margin: 0;
    max-width: 900px;
    margin: 0 auto;
    counter-reset: step-counter;
  }

  li {
    counter-increment: step-counter;
    position: relative;
    padding: 2rem 0 2rem 4rem;
    border-left: 2px solid rgba(129, 140, 248, 0.3);

    &:not(:last-child) {
      border-left: 2px solid rgba(129, 140, 248, 0.3);
    }

    &::before {
      content: counter(step-counter);
      position: absolute;
      left: -1rem;
      top: 2rem;
      width: 2.5rem;
      height: 2.5rem;
      background: linear-gradient(135deg, #818cf8 0%, #6366f1 100%);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 1.125rem;
    }

    .step-title {
      font-size: 1.5rem;
      font-weight: 700;
      margin-bottom: 0.75rem;
      color: #f1f5f9;
    }

    .step-desc {
      color: #cbd5e1;
      line-height: 1.7;
    }
  }
}

.cta {
  background: rgba(129, 140, 248, 0.1);
  border-radius: 24px;
  padding: 4rem 2rem;
  text-align: center;

  &__content {
    max-width: 700px;
    margin: 0 auto;

    h2 {
      font-size: clamp(2rem, 4vw, 2.75rem);
      font-weight: 700;
      margin-bottom: 1.5rem;
      color: #f1f5f9;
    }

    > p {
      font-size: 1.25rem;
      color: #cbd5e1;
      line-height: 1.7;
      margin-bottom: 2.5rem;
    }
  }
}

.btn {
  display: inline-block;
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s;
  border: none;
  cursor: pointer;
  font-size: 1rem;

  &.primary {
    background: linear-gradient(135deg, #818cf8 0%, #6366f1 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(129, 140, 248, 0.4);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(129, 140, 248, 0.5);
    }

    &.large {
      padding: 1.25rem 3rem;
      font-size: 1.125rem;
    }
  }

  &.ghost {
    background: transparent;
    color: #cbd5e1;
    border: 2px solid rgba(129, 140, 248, 0.3);

    &:hover {
      background: rgba(129, 140, 248, 0.1);
      border-color: rgba(129, 140, 248, 0.5);
    }
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 4rem 1rem;
  }

  .section {
    padding: 3rem 1rem;
  }

  .engines__grid,
  .capabilities__grid {
    grid-template-columns: 1fr;
  }

  .process li {
    padding-left: 3rem;

    &::before {
      left: -0.75rem;
      width: 2rem;
      height: 2rem;
      font-size: 1rem;
    }
  }
}
</style>
